/**
 * Where the SITE is, as opposed to where this app is.
 *
 * The store addresses three things that do not belong to it — the registry, the
 * bundle directory, and the editor it hands an app to for review — so it needs
 * the same `VIEWER_BASE` seam the admin uses for `/files`, not a hardcoded `/`.
 *
 * `base` (Vite) says where THIS app's own assets live (`/marketplace/`).
 * `__SERVER_BASE__` says where the SITE is. They are different questions, and
 * conflating them is what makes a build work on one mount and 404 on another.
 */

/**
 * Resolve the site root — always with a trailing slash, so callers concatenate
 * rather than guess.
 *
 * - `'/'` (the default, `VIEWER_BASE` unset) → the origin root, exactly today's
 *   behaviour: `/plugins/registry.json`, `/admin/`.
 * - an ABSOLUTE base (`'/s/<token>/'`) → that prefix. One front, many sites:
 *   every path the store builds stays inside the site it was served from.
 * - a RELATIVE base (`'./'`, the portable build) → "wherever this is mounted".
 *   The store lives in its OWN directory, so the site root is that directory's
 *   PARENT — the admin is a sibling of the store, not a child of it. Resolving
 *   `'./'` against the app base instead would produce `/marketplace/admin/`,
 *   which is the bug this function exists to prevent.
 */
export function siteRoot(serverBase: string, appBase: string, pageHref: string): string {
  const raw = serverBase || '/';
  if (raw.startsWith('.')) {
    return new URL('../', new URL(appBase, pageHref)).href;
  }
  return raw.endsWith('/') ? raw : `${raw}/`;
}

/**
 * The editor URL for REVIEWING a plugin.
 *
 * Two parameters, because a review needs both halves and the cfg alone is the
 * confusing one: `app-cfg` sets the surface the plugin asked for, and `plugin`
 * names the exact manifest to install. Handing over only the cfg gives an editor
 * showing the declared sections with no plugin in it — which reads as "my panel
 * vanished" rather than "nothing loaded it".
 *
 * `manifestUrl` is optional so a cfg-only link is still expressible (reviewing a
 * deployment's setup rather than a plugin).
 */
export function editorUrl(site: string, cfgUrl: string, manifestUrl?: string): string {
  const q = new URLSearchParams({ 'app-cfg': cfgUrl });
  if (manifestUrl) q.set('plugin', manifestUrl);
  return `${site}admin/?${q.toString()}`;
}

/**
 * A `?editor=` override, validated. It lands in an href, so it is restricted to
 * a path or an http(s) URL: without that, `?editor=javascript:…` turns a crafted
 * link into a click that runs script in this page. Returns null when the value
 * is unusable, so the caller falls back to the site's own editor.
 */
export function parseEditorOverride(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') && !/^https?:\/\//i.test(raw)) return null;
  return raw.endsWith('/') ? raw : `${raw}/`;
}

/**
 * Resolve any URL the registry gave us into one that works FROM ANYWHERE —
 * including from a page at a different path depth, which is the whole point:
 * these URLs get handed to the editor as query parameters, and a root-absolute
 * path means something different under a site mount than it does at the origin.
 *
 * Three shapes, three rules:
 *
 * - **Fully qualified** (`https://store/…`) — left alone. A registry may point
 *   at a foreign store on purpose.
 * - **Relative** (`geo.data.v1/0.3.4/manifest.json`) — resolved against the
 *   REGISTRY, not the page. That is what a data hub writes, precisely so its
 *   file survives whatever mount it is served from.
 * - **Root-absolute** (`/plugins/…`) — re-anchored under the site when there is
 *   one. Under `/s/<token>/` an origin-root path cannot be right: the registry
 *   is the site's own mirror, so its entries belong to that site. At the origin
 *   (`site === '/'`) this is a no-op, which is every deployment today.
 *
 * Always returns an absolute href, so the receiver cannot re-resolve it wrongly.
 */
export function resolveStoreUrl(site: string, registryUrl: string, url: string, pageHref: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) {
    const path = site !== '/' && !url.startsWith(site) ? `${site}${url.slice(1)}` : url;
    return new URL(path, pageHref).href;
  }
  return new URL(url, new URL(registryUrl, pageHref)).href;
}
